/* Naviradio vehicle fit finder.
 * Data: assets/nr-fitment.json (generated from the fitment table).
 * The chosen car is kept in localStorage ('nr-car') and shared by the finder,
 * the results section, the product page badge and the "fits your car" card chips. */
(function () {
  if (window.NRFit) return;
  const CFG = window.NR_FIT || {};
  const KEY = 'nr-car';
  let dataPromise = null;

  function load() {
    if (!dataPromise) {
      dataPromise = fetch(CFG.url)
        .then((r) => r.json())
        .then((d) => {
          const rows = d.rows.map((r) => ({ b: r[0], m: r[1], ch: r[2], y1: r[3], y2: r[4], sys: r[5], t: r[6], h: r[7], listed: !!r[8], note: r[9], g: r[10] ? r[10].split('|') : [] }));
          rows.gens = d.gens || {};
          return rows;
        });
    }
    return dataPromise;
  }

  function getCar() {
    try {
      const c = JSON.parse(localStorage.getItem(KEY) || 'null');
      return c && c.brand && c.model ? c : null;
    } catch (e) {
      return null;
    }
  }
  function setCar(c) {
    try {
      if (c) localStorage.setItem(KEY, JSON.stringify(c));
      else localStorage.removeItem(KEY);
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('nr:car', { detail: c }));
  }

  const sysTokens = (s) => (!s || s === 'not stated' ? [] : s.split(' / ').map((t) => t.trim()).filter(Boolean));
  const inYear = (r, y) => y == null || (y >= (r.y1 == null ? -Infinity : r.y1) && y <= (r.y2 == null ? Infinity : r.y2));
  const uniq = (a) => a.filter((v, i) => a.indexOf(v) === i);
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const inGen = (r, gen) => !gen || gen === 'unsure' || !r.g.length || r.g.includes(gen);
  // Brands where we never ask for the factory system: the team picks the exact version from the customer's photos after the order.
  const NO_SYS = ['Mercedes-Benz'];
  const askSys = (b) => !NO_SYS.includes(b);
  const carName = (c) => [c.brand, c.model, c.gen && c.gen !== 'unsure' ? c.gen : '', c.year, c.sys && c.sys !== 'unsure' && askSys(c.brand) ? '· ' + c.sys : ''].filter(Boolean).join(' ');

  function rowsForCar(rows, c) {
    let rs = rows.filter((r) => r.b === c.brand && r.m === c.model && inYear(r, c.year) && inGen(r, c.gen));
    if (c.sys && c.sys !== 'unsure' && askSys(c.brand)) rs = rs.filter((r) => r.t === 'acc' || !sysTokens(r.sys).length || sysTokens(r.sys).includes(c.sys));
    return rs;
  }
  function universal(rows, c) {
    // Android AI box: only for cars from 2016, and never for BMW (not needed there)
    return rows.filter((r) => r.b === 'All brands' && (r.t !== 'aibox' || (c.brand !== 'BMW' && (c.year == null || c.year >= 2016))));
  }
  function fits(rows, handle, c) {
    const own = rows.filter((r) => r.h === handle);
    if (!own.length || own.every((r) => r.b === 'All brands')) return null;
    return rowsForCar(rows, c).some((r) => r.h === handle);
  }

  const root = (CFG.root || '/').replace(/\/$/, '');
  const productCache = {};
  function product(h) {
    if (!productCache[h]) {
      productCache[h] = fetch(`${root}/products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    }
    return productCache[h];
  }
  function money(cents) {
    const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || CFG.shopCurrency || 'EUR';
    if (CFG.moneyFormat && cur === CFG.shopCurrency) {
      const fmt = (n, dec, th, ds) => {
        const parts = (n / 100).toFixed(dec).split('.');
        return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th) + (parts[1] ? ds + parts[1] : '');
      };
      return CFG.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) =>
        ({
          amount: fmt(cents, 2, ',', '.'),
          amount_no_decimals: fmt(cents, 0, ',', '.'),
          amount_with_comma_separator: fmt(cents, 2, '.', ','),
          amount_no_decimals_with_comma_separator: fmt(cents, 0, '.', ','),
          amount_with_apostrophe_separator: fmt(cents, 2, "'", '.'),
        }[k] || fmt(cents, 2, ',', '.'))
      ).replace(/<[^>]+>/g, '');
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' ' + cur;
    }
  }

  /* ------------------------------------------------------------ finder console */
  class NrFitFinder extends HTMLElement {
    connectedCallback() {
      this.s = JSON.parse(this.querySelector('[data-fit-strings]').textContent);
      this.hints = this.s.hints || {};
      this.steps = this.querySelector('[data-fit-steps]');
      this.q = this.querySelector('[data-fit-q]');
      const saved = getCar();
      this.state = saved ? Object.assign({ gen: null }, saved) : { brand: null, model: null, year: null, gen: null, sys: null };
      if (!askSys(this.state.brand)) this.state.sys = null;
      this.addEventListener('click', (e) => this.onClick(e));
      load().then((rows) => {
        this.rows = rows;
        // A car saved before the generation step existed may now need one more answer.
        if (saved && this.current() !== 'done') setCar(null);
        this.render();
      });
    }

    brands() {
      // best-known brands first (our core BMW / Mercedes / Audi / VW on top); brands not in the list follow A-Z
      const top = ['BMW', 'Mercedes-Benz', 'Audi', 'Volkswagen', 'Toyota', 'Ford', 'Porsche', 'Volvo', 'Honda', 'Land Rover', 'Lexus', 'Mazda', 'Skoda', 'Opel', 'Peugeot', 'Jaguar', 'Citroen', 'Seat'];
      const rank = (b) => (top.indexOf(b) + 1 || top.length + 1);
      return uniq(this.rows.filter((r) => r.b !== 'All brands' && r.t !== 'acc').map((r) => r.b)).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
    }
    models(b) {
      const m = {};
      this.rows.forEach((r) => {
        if (r.b === b && r.t !== 'acc') (m[r.m] = m[r.m] || []).push(r);
      });
      return Object.keys(m)
        .sort((a, c) => a.localeCompare(c, undefined, { numeric: true }))
        .map((k) => {
          const ys = m[k].map((r) => r.y1).filter((v) => v != null);
          const ye = m[k].map((r) => r.y2).filter((v) => v != null);
          return { name: k, from: ys.length ? Math.min(...ys) : null, to: ye.length ? Math.max(...ye) : null, open: m[k].some((r) => r.y2 == null) };
        });
    }
    years() {
      const rs = this.rows.filter((r) => r.b === this.state.brand && r.m === this.state.model && r.t !== 'acc');
      if (rs.every((r) => r.y1 == null && r.y2 == null)) return null;
      const lo = Math.min(...rs.map((r) => (r.y1 == null ? 9999 : r.y1)));
      const hi = Math.max(...rs.map((r) => (r.y2 == null ? Math.max(r.y1 || 0, new Date().getFullYear() - 2) : r.y2)));
      const out = [];
      for (let y = lo; y <= hi; y++) if (rs.some((r) => inYear(r, y))) out.push(y);
      return out;
    }
    gens() {
      // Generations sold in the chosen year that have their own units. Asked only when there are two or more.
      const st = this.state;
      const all = this.rows.gens[st.brand + '|' + st.model];
      if (!all || st.year == null) return [];
      const rs = this.rows.filter((r) => r.b === st.brand && r.m === st.model && r.t !== 'acc' && inYear(r, st.year));
      const out = all.filter((g) => (g[1] == null || st.year >= g[1]) && (g[2] == null || st.year <= g[2]) && rs.some((r) => r.g.includes(g[0])));
      return out.length > 1 ? out : [];
    }
    systems() {
      if (!askSys(this.state.brand)) return [];
      const rs = this.rows.filter((r) => r.b === this.state.brand && r.m === this.state.model && inYear(r, this.state.year) && inGen(r, this.state.gen) && r.t !== 'acc');
      const toks = uniq([].concat(...rs.map((r) => sysTokens(r.sys))));
      if (toks.length < 2) return [];
      // Only ask when the answer changes which products fit.
      const sets = toks.map((t) => uniq(rs.filter((r) => !sysTokens(r.sys).length || sysTokens(r.sys).includes(t)).map((r) => r.h)).sort().join('|'));
      return sets.every((x) => x === sets[0]) ? [] : toks;
    }
    current() {
      const st = this.state;
      if (!st.brand) return 'brand';
      if (!st.model) return 'model';
      const ys = this.years();
      if (ys && st.year == null) return 'year';
      if (!st.gen && this.gens().length) return 'gen';
      if (this.systems().length && !st.sys) return 'sys';
      return 'done';
    }

    render() {
      const s = this.s, st = this.state, cur = this.current();
      const order = ['brand', 'model', 'year', 'sys', 'done'];
      const ci = cur === 'gen' ? 2 : order.indexOf(cur);
      const ys = st.model ? this.years() : [];
      const needSys = st.model && (st.year != null || ys === null) ? this.systems().length > 0 || !!st.sys : true;
      const items = [
        ['brand', s.step_brand, st.brand || s.choose],
        ['model', s.step_model, st.model || s.choose],
        ['year', s.step_year, ys === null ? s.any_year : st.year != null ? st.year + (st.gen && st.gen !== 'unsure' ? ' · ' + st.gen : '') : s.choose],
        ['sys', s.step_screen, !needSys ? s.not_needed : st.sys ? (st.sys === 'unsure' ? s.not_sure : st.sys) : s.choose],
      ];
      this.steps.innerHTML = items
        .map((it, i) => {
          const cls = ['nr-ff__step', i < ci ? 'is-done' : '', order[i] === cur || (cur === 'gen' && i === 2) ? 'is-now' : '', (it[0] === 'sys' && !needSys) || (it[0] === 'year' && ys === null) ? 'is-skip' : ''].join(' ');
          return `<button type="button" class="${cls}" data-go="${it[0]}" ${i > ci ? 'disabled' : ''}><span>${i + 1} · ${esc(it[1])}</span><b>${esc(it[2])}</b></button>`;
        })
        .join('');

      let h = '';
      if (cur === 'brand') {
        h = `<p class="nr-ff__title">${esc(s.q_brand)}</p><div class="nr-ff__opts">${this.brands().map((b) => `<button type="button" class="nr-ff__opt" data-brand="${esc(b)}"><b>${esc(b)}</b></button>`).join('')}</div>`;
      } else if (cur === 'model') {
        h = `<p class="nr-ff__title">${esc(s.q_model.replace('[brand]', st.brand))}</p><div class="nr-ff__opts">${this.models(st.brand)
          .map((m) => {
            const yr = m.from ? m.from + (m.open ? '+' : m.to && m.to !== m.from ? '–' + m.to : '') : '';
            return `<button type="button" class="nr-ff__opt" data-model="${esc(m.name)}"><b>${esc(m.name)}</b>${yr ? `<small>${yr}</small>` : ''}</button>`;
          })
          .join('')}</div>`;
      } else if (cur === 'year') {
        h = `<p class="nr-ff__title">${esc(s.q_year.replace('[car]', st.brand + ' ' + st.model))}</p><div class="nr-ff__opts nr-ff__opts--years">${this.years()
          .map((y) => `<button type="button" class="nr-ff__opt" data-year="${y}">${y}</button>`)
          .join('')}</div>`;
      } else if (cur === 'gen') {
        const car = st.brand + ' ' + st.model;
        h = `<p class="nr-ff__title">${esc((s.q_gen || 'Which [car] do you have?').replace('[car]', car).replace('[year]', st.year))}</p><p class="nr-ff__hint">${esc((s.q_gen_hint || '').replace('[year]', st.year))}</p><div class="nr-ff__opts nr-ff__opts--sys">${this.gens()
          .map((g) => `<button type="button" class="nr-ff__opt" data-gen="${esc(g[0])}"><b>${esc(car + ' ' + g[0])}</b><small class="nr-ff__desc">${esc((s.gen_built || 'Built [years]').replace('[years]', g[1] + '–' + (g[2] || '')))}</small></button>`)
          .join('')}<button type="button" class="nr-ff__opt nr-ff__opt--unsure" data-gen="unsure"><b>${esc(s.not_sure)}</b><small class="nr-ff__desc">${esc(s.gen_unsure_text || s.not_sure_text)}</small></button></div>`;
      } else if (cur === 'sys') {
        h = `<p class="nr-ff__title">${esc(s.q_screen)}</p><p class="nr-ff__hint">${esc(s.q_screen_hint)}</p><div class="nr-ff__opts nr-ff__opts--sys">${this.systems()
          .map((x) => {
            const hint = this.hints[x] || {};
            return `<button type="button" class="nr-ff__opt${hint.img ? ' has-img' : ''}" data-sys="${esc(x)}">${hint.img ? `<img src="${esc(hint.img)}" alt="" loading="lazy">` : ''}<b>${esc(hint.title || x)}</b><small class="nr-ff__desc">${esc(hint.text || s.generic_hint)}</small></button>`;
          })
          .join('')}<button type="button" class="nr-ff__opt nr-ff__opt--unsure" data-sys="unsure"><b>${esc(s.not_sure)}</b><small class="nr-ff__desc">${esc(s.not_sure_text)}</small></button></div>`;
      } else {
        h = `<p class="nr-ff__title">${esc(s.done_title.replace('[car]', carName(st)))}</p><p class="nr-ff__hint">${esc(s.done_text)}</p><a class="nr-btn nr-btn--primary nr-ff__see" href="#nr-fit-results">${esc(s.see_results)}</a>`;
      }
      h += `<div class="nr-ff__foot">${st.brand ? `<button type="button" class="nr-ff__link" data-reset>${esc(s.start_again)}</button>` : '<span></span>'}<a href="${esc(s.help_url)}">${esc(s.help_label)}</a></div>`;
      this.q.innerHTML = h;
    }

    onClick(e) {
      const t = e.target.closest('button');
      if (!t || !this.rows) return;
      const st = this.state;
      if (t.hasAttribute('data-reset')) {
        this.state = { brand: null, model: null, year: null, gen: null, sys: null };
        setCar(null);
        this.render();
        return;
      }
      if (t.dataset.brand) this.state = { brand: t.dataset.brand, model: null, year: null, gen: null, sys: null };
      else if (t.dataset.model) Object.assign(st, { model: t.dataset.model, year: null, gen: null, sys: null });
      else if (t.dataset.year) Object.assign(st, { year: +t.dataset.year, gen: null, sys: null });
      else if (t.dataset.gen) Object.assign(st, { gen: t.dataset.gen, sys: null });
      else if (t.dataset.sys) st.sys = t.dataset.sys;
      else if (t.dataset.go) {
        const g = t.dataset.go;
        if (g === 'brand') this.state = { brand: null, model: null, year: null, gen: null, sys: null };
        if (g === 'model') Object.assign(st, { model: null, year: null, gen: null, sys: null });
        if (g === 'year') Object.assign(st, { year: null, gen: null, sys: null });
        if (g === 'sys') st.sys = null;
      } else return;
      const done = this.current() === 'done';
      setCar(done ? this.state : null);
      this.render();
      if (done && !t.dataset.go) {
        const res = document.getElementById('nr-fit-results');
        if (res) res.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }
    }
  }

  /* ------------------------------------------------------------ results */
  class NrFitResults extends HTMLElement {
    connectedCallback() {
      this.s = JSON.parse(this.querySelector('[data-fit-strings]').textContent);
      this.out = this.querySelector('[data-fit-out]');
      document.addEventListener('nr:car', () => {
        this.tab = 'all';
        this.render();
      });
      this.out.addEventListener('click', (e) => {
        const t = e.target.closest('[data-tab]');
        if (t) this.showTab(t.dataset.tab);
      });
      this.tab = 'all';
      this.render();
    }
    showTab(key) {
      this.tab = key;
      this.out.querySelectorAll('[data-tab]').forEach((b) => b.setAttribute('aria-selected', b.dataset.tab === key));
      this.out.querySelectorAll('.nr-fr__group').forEach((g) => (g.hidden = key !== 'all' && g.dataset.key !== key));
    }
    async render() {
      const c = getCar();
      this.hidden = !c;
      if (!c) return;
      const s = this.s;
      const rows = await load();
      const list = {};
      rowsForCar(rows, c)
        .concat(universal(rows, c).filter((r) => r.t === 'aibox'))
        .forEach((r) => {
          if (!list[r.h] || r.listed) list[r.h] = r;
        });
      const items = Object.values(list);
      const main = items.filter((r) => r.t !== 'acc' && r.t !== 'aibox').length;
      let h = `<div class="nr-fr__car"><div><span class="nr-head__kicker">${esc(s.your_car)}</span><h2>${esc(carName(c))}</h2><p>${esc(s.count.replace('[n]', main))}${c.sys === 'unsure' ? ' ' + esc(s.unsure_note) : ''}</p></div><a class="nr-btn nr-btn--outline" href="#nr-finder" data-change>${esc(s.change_car)}</a></div>`;
      const groups = s.groups.filter((g) => items.some((r) => r.t === g.key));
      if (!groups.length) h += `<div class="nr-fr__empty">${esc(s.empty)}</div>`;
      const count = (k) => items.filter((r) => r.t === k).length;
      const split = (label) => {
        const i = label.indexOf(' · ');
        return i > 0 ? [label.slice(0, i), label.slice(i + 3)] : ['', label];
      };
      if (groups.length > 1) {
        h += `<div class="nr-fr__tabs" role="tablist"><button type="button" role="tab" data-tab="all" aria-selected="true"><small>${esc(s.tab_all_kicker || 'Show')}</small><b>${esc(s.tab_all || 'All levels')}</b><span>${items.length}</span></button>${groups
          .map((g) => {
            const [kick, name] = split(g.label);
            return `<button type="button" role="tab" data-tab="${g.key}" aria-selected="false"><small>${esc(kick || s.tab_extra || 'Extra')}</small><b>${esc((s.tab_names || {})[g.key] || name)}</b><span>${count(g.key)}</span></button>`;
          })
          .join('')}</div>`;
      }
      groups.forEach((g) => {
        const [kick, name] = split(g.label);
        h += `<section class="nr-fr__group nr-fr__group--${g.key}" data-key="${g.key}"><div class="nr-fr__ghead">${kick ? `<span class="nr-fr__level">${esc(kick)}</span>` : ''}<h3>${esc(name)}</h3><p>${esc(g.text)} <b>${esc(g.best)}</b></p></div><ul class="nr-fr__cards">${items
          .filter((r) => r.t === g.key)
          .map((r) => `<li data-h="${esc(r.h)}" class="nr-fr__card is-loading"><span class="nr-fr__fit">${esc(s.fits)}: ${esc([r.m, r.ch, r.y1 || r.y2 ? (r.y1 || '') + (r.y2 ? '–' + r.y2 : '+') : '', r.sys && r.sys !== 'not stated' ? r.sys : ''].filter(Boolean).join(' · '))}</span>${r.note ? `<span class="nr-fr__note">${esc(r.note)}</span>` : ''}<span class="nr-fr__tag ${r.listed ? 'is-ok' : 'is-check'}">${esc(r.listed ? s.tag_listed : s.tag_check)}</span></li>`)
          .join('')}</ul></section>`;
      });
      h += `<div class="nr-fr__help"><div><h3>${esc(s.help_title)}</h3><p>${esc(s.help_text)}</p></div><a class="nr-btn nr-btn--ghost" href="${esc(s.help_url)}">${esc(s.help_button)}</a></div>`;
      this.out.innerHTML = h;
      this.out.querySelectorAll('[data-h]').forEach(async (li) => {
        const p = await product(li.dataset.h);
        if (!p) {
          li.remove();
          return;
        }
        const img = p.featured_image ? `<img src="${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=480" alt="${esc(p.title)}" loading="lazy" width="480" height="360">` : '';
        li.classList.remove('is-loading');
        li.innerHTML = `<a href="${root}/products/${p.handle}">${img}<div class="nr-fr__body">${li.innerHTML}<h4>${esc(p.title)}</h4><span class="nr-fr__price">${p.price_varies ? esc(s.from) + ' ' : ''}${money(p.price_min || p.price)}</span>${p.available ? '' : `<span class="nr-fr__note">${esc(s.sold_out)}</span>`}</div></a>`;
      });
    }
  }

  /* ------------------------------------------------------------ product page badge */
  class NrFitBadge extends HTMLElement {
    connectedCallback() {
      this.s = JSON.parse(this.querySelector('[data-fit-strings]').textContent);
      this.out = this.querySelector('[data-fit-out]');
      document.addEventListener('nr:car', () => this.render());
      this.render();
    }
    async render() {
      const s = this.s, c = getCar(), finder = s.finder_url;
      const rows = await load();
      const own = rows.filter((r) => r.h === this.dataset.handle);
      if (!own.length || own.every((r) => r.b === 'All brands')) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      if (!c) {
        this.className = 'nr-fitb is-none';
        this.out.innerHTML = `<span>${esc(s.ask)}</span><a href="${finder}">${esc(s.ask_link)}</a>`;
        return;
      }
      const ok = fits(rows, this.dataset.handle, c);
      this.className = 'nr-fitb ' + (ok ? 'is-yes' : 'is-no');
      this.out.innerHTML = ok
        ? `<b>✓ ${esc(s.yes.replace('[car]', carName(c)))}</b><a href="${finder}">${esc(s.change)}</a>`
        : `<b>${esc(s.no.replace('[car]', carName(c)))}</b><a href="${s.results_url}">${esc(s.no_link)}</a>`;
    }
  }

  /* ------------------------------------------------------------ "fits your car" chips on product cards
   * Each card is checked once (data-nr-fit). Chips are only rebuilt when the chosen car changes,
   * so lazy-loaded images and app updates while scrolling never touch the cards again. */
  async function chips(reset) {
    if (reset) {
      document.querySelectorAll('.nr-fitchip').forEach((el) => el.remove());
      document.querySelectorAll('[data-nr-fit]').forEach((el) => el.removeAttribute('data-nr-fit'));
    }
    const c = getCar();
    if (!c) return;
    const cards = document.querySelectorAll('.card-wrapper:not([data-nr-fit])');
    if (!cards.length) return;
    const rows = await load();
    cards.forEach((card) => {
      card.setAttribute('data-nr-fit', '');
      const a = card.querySelector('a[href*="/products/"]');
      const m = a && a.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!m || fits(rows, decodeURIComponent(m[1]), c) !== true) return;
      const info = card.querySelector('.card__information');
      if (!info) return;
      const chip = document.createElement('span');
      chip.className = 'nr-fitchip';
      chip.textContent = '✓ ' + (CFG.chip || 'Fits your car');
      info.prepend(chip);
    });
  }
  let chipTimer;
  const queueChips = (reset) => {
    clearTimeout(chipTimer);
    chipTimer = setTimeout(() => chips(reset === true), 200);
  };
  const hasNewCards = (muts) =>
    muts.some((m) =>
      Array.prototype.some.call(m.addedNodes, (n) => n.nodeType === 1 && (n.classList.contains('card-wrapper') || n.getElementsByClassName('card-wrapper').length))
    );
  document.addEventListener('nr:car', () => queueChips(true));
  document.addEventListener('DOMContentLoaded', () => {
    queueChips();
    if (!getCar()) return;
    const target = document.getElementById('MainContent') || document.body;
    new MutationObserver((muts) => {
      if (hasNewCards(muts)) queueChips();
    }).observe(target, { childList: true, subtree: true });
  });

  /* ------------------------------------------------------------ model chips in the product "Fits your car" panel */
  async function modelChips() {
    const boxes = document.querySelectorAll('[data-fit-models]');
    if (!boxes.length) return;
    const rows = await load();
    const c = getCar();
    boxes.forEach((box) => {
      const own = rows.filter((r) => r.h === box.dataset.handle && r.b !== 'All brands' && r.t !== 'acc');
      if (!own.length) {
        box.hidden = true;
        return;
      }
      const brands = uniq(own.map((r) => r.b));
      const groups = {};
      own.forEach((r) => {
        const k = r.b + '|' + r.m;
        const g = (groups[k] = groups[k] || { b: r.b, m: r.m, y1: null, y2: null, open: false, listed: true, rows: [] });
        g.rows.push(r);
        if (r.y1 != null) g.y1 = g.y1 == null ? r.y1 : Math.min(g.y1, r.y1);
        if (r.y2 != null) g.y2 = g.y2 == null ? r.y2 : Math.max(g.y2, r.y2);
        if (r.y2 == null && r.y1 != null) g.open = true;
        if (!r.listed) g.listed = false;
      });
      const chips = Object.values(groups)
        .sort((a, b) => (a.b + a.m).localeCompare(b.b + b.m, undefined, { numeric: true }))
        .map((g) => {
          const yrs = g.y1 != null ? ' ' + g.y1 + (g.open ? '+' : g.y2 && g.y2 !== g.y1 ? '–' + g.y2 : '') : '';
          const mine = c && c.brand === g.b && c.model === g.m && g.rows.some((r) => inYear(r, c.year));
          const name = (brands.length > 1 ? g.b + ' ' : '') + g.m;
          return `<span class="nr-fit__chip${mine ? ' is-mine' : ''}${g.listed ? '' : ' is-check'}">${mine ? '✓ ' : ''}${esc(name)}${esc(yrs)}</span>`;
        });
      const systems = uniq([].concat(...own.map((r) => sysTokens(r.sys)))).filter((x) => x !== 'no screen');
      const anyCheck = Object.values(groups).some((g) => !g.listed);
      box.innerHTML =
        `<div class="nr-fit__chips">${chips.join('')}</div>` +
        (systems.length ? `<p class="nr-fit__sys"><span>${esc(box.dataset.systemLabel)}</span> ${esc(systems.join(' · '))}</p>` : '') +
        (anyCheck ? `<p class="nr-fit__confirm">${esc(box.dataset.confirmLabel)}</p>` : '');
      box.hidden = false;
      const legacy = box.parentElement.querySelector('.nr-fit__models');
      if (legacy) legacy.hidden = true;
    });
  }
  document.addEventListener('DOMContentLoaded', modelChips);
  document.addEventListener('nr:car', modelChips);

  /* ------------------------------------------------------------ "fits your car" line in the cart drawer */
  async function cartFit() {
    const c = getCar();
    const items = document.querySelectorAll('cart-drawer .cart-item');
    if (!items.length) return;
    const rows = c ? await load() : null;
    items.forEach((item) => {
      const old = item.querySelector('.nr-cart-fit');
      if (old) old.remove();
      if (!c) return;
      const a = item.querySelector('a.cart-item__name') || item.querySelector('.cart-item__details a[href*="/products/"]');
      const m = a && a.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!m || fits(rows, decodeURIComponent(m[1]), c) !== true) return;
      const line = document.createElement('span');
      line.className = 'nr-cart-fit';
      line.textContent = '✓ ' + (CFG.cartFits || 'Fits your [car]').replace('[car]', carName(c));
      a.insertAdjacentElement('afterend', line);
    });
  }
  document.addEventListener('DOMContentLoaded', () => {
    cartFit();
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    let t;
    new MutationObserver((muts) => {
      if (muts.some((mu) => Array.prototype.some.call(mu.addedNodes, (n) => n.nodeType === 1 && !n.classList.contains('nr-cart-fit')))) {
        clearTimeout(t);
        t = setTimeout(cartFit, 120);
      }
    }).observe(drawer, { childList: true, subtree: true });
  });
  document.addEventListener('nr:car', cartFit);

  /* ------------------------------------------------------------ hero "Find my car's unit" button */
  function syncCta() {
    const c = getCar();
    document.querySelectorAll('[data-fit-cta]').forEach((a) => {
      // On the finder page the button scrolls; elsewhere (homepage hero) it goes to the finder page.
      if (c) {
        a.textContent = a.dataset.labelCar.replace('[car]', c.brand + ' ' + c.model);
        a.setAttribute('href', document.getElementById('nr-fit-results') ? '#nr-fit-results' : a.dataset.hrefCar || a.dataset.href || '#nr-fit-results');
      } else {
        a.textContent = a.dataset.label;
        a.setAttribute('href', document.getElementById('nr-finder') ? '#nr-finder' : a.dataset.href || '#nr-finder');
      }
    });
  }
  const calm = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Show the finder without hiding the hero: stay at the top when the whole finder fits on screen there,
  // otherwise centre it. Then pulse it and focus the first answer.
  function openFinder(instant, focusFirst) {
    const target = document.getElementById('nr-finder');
    if (!target) return false;
    const absTop = target.getBoundingClientRect().top + window.scrollY;
    const fitsAtTop = absTop + target.offsetHeight <= window.innerHeight;
    const behavior = instant || calm() ? 'auto' : 'smooth';
    if (fitsAtTop) window.scrollTo({ top: 0, behavior });
    else target.scrollIntoView({ behavior, block: 'center' });
    target.classList.remove('is-pulse');
    void target.offsetWidth;
    target.classList.add('is-pulse');
    setTimeout(() => target.classList.remove('is-pulse'), 1600);
    // Move focus only for keyboard users; mouse and link arrivals just see the pulse.
    if (focusFirst) {
      const first = target.querySelector('.nr-ff__opt') || target.querySelector('.nr-ff__step.is-now');
      if (first) setTimeout(() => first.focus({ preventScroll: true }), 450);
    }
    return true;
  }
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (href === '#nr-fit-results') {
      const res = document.getElementById('nr-fit-results');
      if (res && !res.hidden) {
        e.preventDefault();
        res.scrollIntoView({ behavior: calm() ? 'auto' : 'smooth', block: 'start' });
      }
      return;
    }
    if (href === '#nr-finder' || a.hasAttribute('data-fit-cta')) {
      if (document.getElementById('nr-finder')) {
        e.preventDefault();
        openFinder(false, e.detail === 0);
      }
    }
  });
  // Arriving from another page (?finder=1 or an old #nr-finder link)
  function openFromUrl() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('finder') && url.hash !== '#nr-finder') return;
    url.searchParams.delete('finder');
    if (url.hash === '#nr-finder') url.hash = '';
    history.replaceState(null, '', url.pathname + url.search + url.hash);
    const go = () => openFinder(true, false);
    go();
    window.addEventListener('load', () => setTimeout(go, 50), { once: true });
  }
  document.addEventListener('DOMContentLoaded', openFromUrl);
  document.addEventListener('DOMContentLoaded', syncCta);
  document.addEventListener('nr:car', syncCta);

  customElements.define('nr-fit-finder', NrFitFinder);
  customElements.define('nr-fit-results', NrFitResults);
  customElements.define('nr-fit-badge', NrFitBadge);
  window.NRFit = { getCar, setCar, load };
})();
